
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, Lock, Eye, Heart, Clock, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTimedAccess } from "@/context/TimedAccessContext";

interface ResourceCardProps {
  title: string;
  description: string;
  category: string;
  isPremium: boolean;
  link: string;
  readTime?: string;
  readCount?: number;
  likeCount?: number;
  comingSoon?: boolean;
  isPreviewEnabled?: boolean;
  renderAsButton?: boolean;
}

export default function ResourceCard({ 
  title, 
  description, 
  category, 
  isPremium, 
  link,
  readTime = "10 min read",
  readCount = 0,
  likeCount = 0,
  comingSoon = false,
  isPreviewEnabled = false,
  renderAsButton = false
}: ResourceCardProps) {
  const { isSubscribed } = useAuth();
  const { hasFreeAccess } = useTimedAccess();
  
  console.log("ResourceCard - Rendering card:", {
    title,
    isPremium,
    isSubscribed,
    hasFreeAccess,
    timestamp: new Date().toISOString()
  });
  
  // Check if the link is external (starts with http)
  const isExternalLink = link.startsWith('http');
  
  // Add preview parameter to internal links when isPreviewEnabled is true and it's a premium article
  const articleLink = (!isExternalLink && isPreviewEnabled && isPremium) 
    ? `${link}?preview=true` 
    : link;

  // Check if content is accessible (subscribed OR has free access OR not premium)
  const canAccessContent = !isPremium || isSubscribed || hasFreeAccess || isPreviewEnabled;
  
  console.log("ResourceCard - Access check:", {
    title,
    canAccessContent,
    isPremium,
    isSubscribed,
    hasFreeAccess,
    isPreviewEnabled,
    timestamp: new Date().toISOString()
  });

  // For the "Read Now" button-only rendering option 
  if (renderAsButton) {
    // Handle coming soon state
    if (comingSoon) {
      return (
        <Button variant="outline" disabled>
          Coming Soon
        </Button>
      );
    }
    
    // Handle premium content for non-subscribed users without free access
    if (isPremium && !canAccessContent) {
      return (
        <Link to="/pricing">
          <Button variant="default" size="sm" className="flex items-center gap-2">
            Subscribe to Unlock <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      );
    }
    
    // Standard button for accessible content
    return isExternalLink ? (
      <a href={articleLink} target="_blank" rel="noopener noreferrer">
        <Button variant="default" size="sm" className="flex items-center gap-2">
          Read Now <ArrowRight className="h-4 w-4" />
        </Button>
      </a>
    ) : (
      <Link to={articleLink}>
        <Button variant="default" size="sm" className="flex items-center gap-2">
          Read Now <ArrowRight className="h-4 w-4" />
        </Button>
      </Link>
    );
  }

  // Standard card rendering (original behavior)
  return (
    <Card className="h-full flex flex-col relative">
      {/* Add Coming Soon overlay for articles marked as coming soon */}
      {comingSoon && (
        <div className="absolute inset-0 bg-black/50 z-10 flex items-center justify-center">
          <Badge variant="default" className="bg-amber-500 text-white flex items-center gap-2">
            <Clock className="h-4 w-4" /> Coming Soon
          </Badge>
        </div>
      )}

      <CardHeader>
        <div className="flex justify-between items-start gap-2 mb-2">
          <Badge variant={isPremium ? "default" : "outline"} className="bg-gradient text-white">
            {category}
          </Badge>
          {isPremium && !isPreviewEnabled && !hasFreeAccess && (
            <Lock className="h-4 w-4 text-muted-foreground" />
          )}
          {isPreviewEnabled && (
            <Badge variant="outline" className="text-primary border-primary">
              Preview
            </Badge>
          )}
          {isPremium && hasFreeAccess && (
            <Badge variant="outline" className="text-green-600 border-green-600">
              Free Access
            </Badge>
          )}
        </div>
        <CardTitle className="leading-tight">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex-grow">
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>{readTime}</span>
          {!isPremium && (
            <>
              <div className="flex items-center gap-1">
                <Eye className="h-4 w-4" /> {readCount}
              </div>
              <div className="flex items-center gap-1">
                <Heart className="h-4 w-4" /> {likeCount}
              </div>
            </>
          )}
          {comingSoon && (
            <div className="flex items-center gap-1 text-amber-500 font-medium">
              <Clock className="h-4 w-4" /> Coming Soon
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter>
        {comingSoon ? (
          <Button variant="outline" className="w-full" disabled>
            Coming Soon
          </Button>
        ) : (!canAccessContent) ? (
          <Link to="/pricing" className="w-full">
            <Button variant="outline" className="w-full">
              Subscribe to Unlock
            </Button>
          </Link>
        ) : (
          <>
            {isExternalLink ? (
              <a href={articleLink} target="_blank" rel="noopener noreferrer" className="w-full">
                <Button className="w-full flex items-center gap-2">
                  Read Now <ExternalLink size={16} />
                </Button>
              </a>
            ) : (
              <Link to={articleLink} className="w-full">
                <Button className="w-full">
                  Read Now
                </Button>
              </Link>
            )}
          </>
        )}
      </CardFooter>
    </Card>
  );
}
